import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router";
import { Col, Row } from "reactstrap";
import {
  getFormattedContactNumber,
  getFormattedOptions,
  getModifiedObject,
  Toast,
  uploadImage,
} from "utility/helpers";
import { fetchAllRegions } from "services/services.congressman";
import { fetchPartylists } from "services/services.affiliation";
import { genericPost, genericPut } from "services/services.general";
import { fetchCandidates } from "services/services.official";
import ImgContainer from "components/common/img";
import defaultUserImage from "assets/img/default_user.png";
import { FILE_TYPE_IMAGE } from "constants/fileType";
import {
  FormContainer,
  FormHeaderSection,
  FormSection,
  HeaderText,
  ImageWrapper,
  UploadButton,
  UploadInput,
} from "assets/js/Styles";
import { PARTYLIST } from "utility/constants";
import DirectoryForm from "../Form";
import { POSITION_SENATOR } from "../constants";
import { validateOfficialDirectoryForm } from "./utility";

const EditSenator = () => {
  const location = useLocation();
  const sen = location.state?.senator || null;

  const [isLoading, setIsLoading] = useState(false);
  const [partylists, setPartylists] = useState([]);
  const [selectedPartylist, setSelectedPartylist] = useState(null);
  const [isEdit, setIsEdit] = useState(false);

  const [regionOptions, setRegionOptions] = useState([]);

  const [senatorOptions, setSenatorOptions] = useState([]);
  const [selectedSenator, setSelectedSenator] = useState(null);

  const [selectedRegion, setSelectedRegion] = useState(null);

  const [formFields, setFormFields] = useState({
    lastName: "",
    firstName: "",
    fullName: "",
    omegaId: "",
    region: "",
    contact: "",
    province: "",
    municipality: "",
    party: "",
    cos: "",
    cosNumber: "",
    photo: "",
    photoName: "",
    cosFirstName: "",
    cosLastName: "",
    name: "",
  });

  const navigate = useNavigate();

  const fetchSenators = async () => {
    await fetchCandidates({
      url: "/election/nominations/?position_id=6&is_winner=true&page_size=30&is_active=true",
      callback: (data) => {
        if (data.results) {
          const rows = data.results;

          const senators = rows.map((senator) => ({
            ...senator,
            value: senator.candidate.id,
            // eslint-disable-next-line max-len
            label: `${senator.candidate.first_name} ${senator.candidate.last_name}`,
          }));

          if (sen) {
            const selectedSen = senators.find(
              (senator) => senator.candidate.id === sen.omegaId
            );
            setSelectedSenator(selectedSen);
          }

          setSenatorOptions(senators);
        }
      },
    });
  };

  const fetchRegion = () => {
    fetchAllRegions({
      callback: (data) => {
        if (data.length) {
          const options = data
            .filter((region) => region.name !== PARTYLIST)
            .map(({ acronym, id, omegaId }) => ({
              omegaId,
              label: acronym,
              value: id,
            }));

          setRegionOptions(options);

          const region = options?.find(
            (option) => option.value === sen?.regionId
          );

          if (region) {
            setSelectedRegion(region);
          } else {
            setFormFields({
              ...formFields,
              region: null,
            });
          }
        }
      },
    });
  };

  const setData = () => {
    const option = partylists.find((partylist) => partylist.id === sen.partyId);

    if (option) setSelectedPartylist(option);

    setIsLoading(false);
  };

  const setSenDetails = () => {
    const updatedFields = {
      ...formFields,
      lastName: sen.lastName,
      firstName: sen.firstName,
      fullName: sen.fullName,
      contact: getFormattedContactNumber(sen.contact),
      party: sen.partyId,
      region: selectedRegion,
      provinceId: sen.provinceId,
      municipalityId: sen.municipalityId,
      cos: sen.cosName,
      cosNumber: getFormattedContactNumber(sen.cosContact),
      photo: sen.photo || "",
      cosFirstName: sen.cosFirstName || sen.cosName,
      cosLastName: sen.cosLastName,
    };

    if (sen.photo) {
      const imgContainer = document.querySelector(".sen-img");
      imgContainer.src = sen.photo;
    }

    setFormFields(updatedFields);
  };

  const sendData = (formData) => {
    setIsLoading(true);

    if (sen) {
      genericPut({
        url: `/official/${sen.id}`,
        formData,
        callback: ({ isSuccess, msg }) => {
          setIsLoading(false);

          if (isSuccess) {
            navigate("/directory", {
              state: { view: POSITION_SENATOR },
            });

            Toast({
              type: 1,
              content: "Successfully edited senator details",
            });
          } else {
            Toast({
              type: 4,
              content: msg,
            });
          }
        },
      });
    } else {
      genericPost({
        url: "/official",
        formData,
        callback: ({ isSuccess, msg }) => {
          setIsLoading(false);

          if (isSuccess) {
            navigate("/directory", {
              state: { view: POSITION_SENATOR },
            });

            Toast({
              type: 1,
              content: "Successfully added senator.",
            });
          } else {
            Toast({
              type: 4,
              content: msg,
            });
          }
        },
      });
    }
  };

  const handleOnSubmit = async () => {
    const isValid = await validateOfficialDirectoryForm(formFields);

    if (isValid.valid) {
      const data = {
        // eslint-disable-next-line max-len
        fullName: `SEN. ${formFields.firstName} ${formFields.lastName}`,
        firstName: formFields.firstName,
        lastName: formFields.lastName,
        contact: formFields.contact?.slice(1),
        regionId: formFields.region.value,
        cosName: `${formFields.cosFirstName} ${formFields.cosLastName}`,
        cosFirstName: formFields.cosFirstName,
        cosLastName: formFields.cosLastName,
        omegaId: formFields.omegaId,
        position: POSITION_SENATOR,
        ...(formFields.cosNumber ? { cosContact: formFields.cosNumber } : {}),
        ...(formFields.province.value !== "All Provinces"
          ? { provinceId: formFields.province.value }
          : {}),
        ...(formFields.municipality.value !== "All Municipalities"
          ? { municipalityId: formFields.municipality.value }
          : {}),
        ...(selectedPartylist ? { partyId: selectedPartylist.value } : {}),
      };

      if (formFields.cosNumber) {
        data.cosContact = formFields.cosNumber?.slice(1);
      }

      let initialData = {};

      if (sen) {
        initialData = {
          firstName: sen.firstName,
          lastName: sen.lastName,
          fullName: sen.fullName,
          contact: sen.contact,
          regionId: sen.regionId,
          provinceId: sen.provinceId,
          municipalityId: sen.municipalityId,
          partyId: sen.partyId,
          cosName: sen.cosName,
          cosContact: sen.cosContact,
          cosFirstName: sen.cosFirstName,
          cosLastName: sen.cosLastName,
          photo: sen.photo,
          omegaId: formFields.omegaId,
          position: POSITION_SENATOR,
        };
      }

      setIsLoading(true);

      if (typeof formFields.photo !== "string") {
        const fileName = await uploadImage({
          photo: formFields.photo,
          photoName: formFields.photoName,
        });
        data.photo = fileName;
      }

      const formData = sen ? await getModifiedObject(initialData, data) : data;

      if (sen) {
        if (formFields.municipality.value === "All Municipalities") {
          formData.municipalityId = null;
        }

        if (formFields.province.value === "All Provinces") {
          formData.provinceId = null;
        }
      }

      await sendData(formData);
    } else {
      Toast({
        type: 4,
        content: isValid.error,
      });
    }
  };

  const fetchData = () => {
    fetchPartylists({
      callback: (data) => {
        const newData = getFormattedOptions("name", data);

        setPartylists(newData);
      },
    });

    setIsLoading(false);
  };

  const handleFileUpload = (evt) => {
    const file = evt.target.files[0];
    const reader = new FileReader();
    const fileTye = file.type.split("/")[0];

    if (fileTye === FILE_TYPE_IMAGE) {
      reader.onloadend = () => {
        const imgContainer = document.querySelector(".sen-img");

        const fileName = sen
          ? `${
              sen.id
            }-${formFields.lastName.toLocaleLowerCase()}-${Date.now()}.${
              file.type.split("/")[1]
            }`
          : `${formFields.lastName.toLocaleLowerCase()}-${Date.now()}.${
              file.type.split("/")[1]
            }`;

        setFormFields({
          ...formFields,
          photo: file,
          photoName: fileName,
        });

        imgContainer.src = reader.result;
      };

      reader.readAsDataURL(file);
    } else {
      Toast({
        type: 4,
        content: "File uploaded must be an image.",
      });
    }
  };

  useEffect(() => {
    setIsLoading(true);
    //  fetch all district, partylist, region
    fetchRegion();
    fetchData();
    fetchSenators();
  }, []);

  useEffect(() => {
    if (sen !== null && partylists.length) {
      setData();
      setSenDetails();
      setIsEdit(true);
    }
  }, [partylists, sen]);

  return (
    <FormContainer>
      <Col className="container">
        <FormHeaderSection>
          <HeaderText>{!sen ? "Add Senator" : "Senator Details"}</HeaderText>
        </FormHeaderSection>

        <FormSection>
          <Row>
            <Col
              xs="12"
              sm="6"
              lg="4"
            >
              <ImageWrapper>
                <ImgContainer
                  alt="sen-img"
                  className="sen-img"
                  src={defaultUserImage}
                  width="196px"
                  height="196px"
                />

                <UploadButton htmlFor="file-upload">
                  {"Upload Photo"}
                  <UploadInput
                    id="file-upload"
                    type="file"
                    onChange={handleFileUpload}
                  />
                </UploadButton>
              </ImageWrapper>
            </Col>
            <Col
              xs="12"
              sm="6"
              lg="8"
            >
              <DirectoryForm
                handleOnSubmit={handleOnSubmit}
                formFields={formFields}
                setFormFields={setFormFields}
                governorOptions={senatorOptions}
                selectedGovernor={selectedSenator}
                setSelectedGovernor={setSelectedSenator}
                selectedRegion={selectedRegion}
                setSelectedRegion={setSelectedRegion}
                regionOptions={regionOptions}
                partylists={partylists}
                selectedPartylist={selectedPartylist}
                setSelectedPartylist={setSelectedPartylist}
                isLoading={isLoading}
                isEdit={isEdit}
                position={POSITION_SENATOR}
              />
            </Col>
          </Row>
        </FormSection>
      </Col>
    </FormContainer>
  );
};

export default EditSenator;
